import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, X } from "lucide-react";

import { api } from "../../lib/api";
import {
  BOOKING_DRIVER_LABEL, DRIVER_STATE_LABEL, LAYER_LABEL, LAYER_STATUS_LABEL, RELEASE_LABEL, errorMessage, humanize, pesos, tone,
} from "../../lib/renterDrivers";
import DriverDetailsForm, { emptyDetails, toPayload } from "./DriverDetailsForm";

// Task 6 staff panel on a self-drive booking: who may drive, why, and whether
// the vehicle may be released. The server decides; this only displays and
// sends explicit actions. There is no "release anyway".
export default function RenterDriverPanel({ bookingId, data, onChanged }) {
  const [info, setInfo] = useState(null);
  const [adding, setAdding] = useState(null);
  const [inviting, setInviting] = useState(false);

  const selfDrive = (data?.rental_service_type || "self_drive") === "self_drive";
  const load = useCallback(() => {
    if (!selfDrive) return;
    api.get(`/renter-drivers/bookings/${bookingId}`).then((r) => setInfo(r.data)).catch((err) => {
      if (err?.response?.status !== 403) toast.error(errorMessage(err));
    });
  }, [bookingId, selfDrive]);
  useEffect(() => { load(); }, [load, data?.item_id, data?.start_datetime, data?.end_datetime]);

  if (!selfDrive || !info || info.service_mode !== "self_drive") return null;
  const caps = info.capabilities || [];
  const can = (c) => caps.includes(c);
  const ev = info.evaluation;
  const release = info.release;
  const changed = () => { setAdding(null); setInviting(false); load(); onChanged?.(); };

  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); changed(); } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4" data-testid="renter-driver-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-lg font-semibold flex items-center gap-2"><KeyRound className="w-5 h-5" /> Self-drive drivers</h2>
        <span className={`text-xs px-2 py-0.5 rounded-full border ${tone(release.release_state)}`} data-testid="renter-release-state">
          {RELEASE_LABEL[release.release_state] || humanize(release.release_state)}
        </span>
      </div>
      <p className="text-xs text-slate-600">
        Renter: {info.renter.name} · {info.renter.is_driving === false ? "not driving" : "driving"} ·{" "}
        {BOOKING_DRIVER_LABEL[ev.summary] || humanize(ev.summary)}
      </p>

      {release.status !== "READY" && (
        <ul className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 space-y-1" data-testid="renter-release-reasons">
          {Object.entries(release.gates).flatMap(([gate, g]) => g.reasons.map((r) => (
            <li key={`${gate}-${r.code}-${r.message}`}><span className="font-medium">{humanize(gate)}:</span> {r.message}</li>
          )))}
        </ul>
      )}

      {!ev.primary && (
        <p className="text-sm text-rose-700" data-testid="renter-driver-required">DRIVER REQUIRED — record the primary authorized driver.</p>
      )}
      {[ev.primary, ...(ev.additional || [])].filter(Boolean).map((d) => (
        <DriverCard key={d.authorized_driver_id} d={d} bookingId={bookingId} can={can} act={act}
                    pickupPolicy={info.pickup_policy} />
      ))}

      {info.fees?.additional_driver_fee_centavos ? (
        <p className="text-sm">Driver fees: {pesos(info.fees.additional_driver_fee_centavos)}</p>
      ) : null}
      {info.invitations?.length > 0 && (
        <p className="text-xs text-slate-600">Invitations: {info.invitations.map((i) => `${i.name} (${i.display_status})`).join(", ")}</p>
      )}

      {can("renter_drivers.manage") && (
        <div className="flex flex-wrap gap-2">
          {!ev.primary && <button className="px-3 py-1.5 rounded-lg btn-brand text-xs" onClick={() => setAdding("PRIMARY")}
                                  data-testid="renter-add-primary">Add primary driver</button>}
          <button className="px-3 py-1.5 rounded-lg border text-xs" onClick={() => setAdding("ADDITIONAL")} data-testid="renter-add-additional">Add driver</button>
          <button className="px-3 py-1.5 rounded-lg border text-xs" onClick={() => setInviting(true)}>Invite driver</button>
        </div>
      )}
      {adding && <AddDriver role={adding} bookingId={bookingId} renterName={info.renter.name} onClose={() => setAdding(null)} onDone={changed} />}
      {inviting && <InviteDriver bookingId={bookingId} onClose={() => setInviting(false)} onDone={changed} />}
      <p className="text-[11px] text-slate-500">Only the authorized drivers listed here may drive. A confirmed booking is not release-ready until every check passes.</p>
    </section>
  );
}

function DriverCard({ d, bookingId, can, act, pickupPolicy }) {
  const base = `/renter-drivers/bookings/${bookingId}/drivers/${d.authorized_driver_id}`;
  const checks = d.pickup_checks || {};
  const issues = (d.reasons || []).filter((r) => r.severity !== "WARNING");
  const check = (payload, ok) => act(() => api.post(`${base}/pickup-check`, payload), ok);

  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`renter-driver-${d.authorized_driver_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium">{d.legal_name || "Invited driver"} <span className="text-xs text-slate-500">({d.role === "PRIMARY" ? "Primary" : "Additional"})</span></div>
          <div className="text-xs text-slate-600">
            {d.licence_masked ? `Licence ${d.licence_masked}` : "No licence number recorded"}
            {d.date_validity?.expiry_date ? ` · valid until ${d.date_validity.expiry_date}` : ""}
            {d.vehicle_category?.result ? ` · vehicle match: ${humanize(d.vehicle_category.result)}` : ""}
          </div>
        </div>
        <span className={`text-[11px] px-2 py-0.5 rounded-full border ${tone(d.status)}`}>{DRIVER_STATE_LABEL[d.status] || humanize(d.status)}</span>
      </div>
      {d.layers && (
        <div className="flex flex-wrap gap-1 text-[11px]">
          {Object.entries(d.layers).map(([layer, status]) => (
            <span key={layer} className={`px-2 py-0.5 rounded border ${tone(status)}`}>{LAYER_LABEL[layer]}: {LAYER_STATUS_LABEL[status]}</span>
          ))}
        </div>
      )}
      {issues.length > 0 && (
        <ul className="text-xs text-slate-700 list-disc pl-4">
          {issues.map((r) => <li key={r.code}>{r.message}{r.source ? ` (${humanize(r.source)})` : ""}</li>)}
        </ul>
      )}
      <div className="text-xs text-slate-600">
        Original licence: {humanize(checks.original_licence?.result) || (pickupPolicy?.original_licence_required ? "Not checked yet" : "Not required")} ·
        Identity: {humanize(checks.identity?.result) || (pickupPolicy?.identity_check_required ? "Not checked yet" : "Not required")}
        {checks.idp ? ` · IDP: ${humanize(checks.idp.result)}` : ""}
      </div>
      <div className="flex flex-wrap gap-2">
        {can("renter_drivers.release_check") && d.profile_id && (
          <>
            <button className="px-2 py-1 rounded border text-xs" onClick={() => check({ original_licence: "PRESENTED", note: "Original licence inspected.", verify_licence_at_counter: can("renter_drivers.document_review") }, "Original licence recorded")}>Original licence seen</button>
            <button className="px-2 py-1 rounded border text-xs" onClick={() => check({ identity: "MATCHED", note: "Person matched the licence." }, "Identity recorded")}>Identity matches</button>
            <button className="px-2 py-1 rounded border text-xs" onClick={() => check({ idp: "PRESENTED", note: "IDP inspected." }, "IDP recorded")}>IDP seen</button>
            <button className="px-2 py-1 rounded border border-rose-300 text-rose-700 text-xs" onClick={() => {
              const note = window.prompt("What did not match? (10 characters or more)");
              if (note) check({ identity: "MISMATCH", note }, "Mismatch recorded — release blocked");
            }}>Identity does not match</button>
          </>
        )}
        {can("renter_drivers.document_review") && d.profile_id && d.status === "BUSINESS_REVIEW_REQUIRED" && (
          <button className="px-2 py-1 rounded border text-xs" onClick={() => {
            const note = window.prompt("What did you check on the licence? (10 characters or more)");
            if (note) act(() => api.post(`/renter-drivers/profiles/${d.profile_id}/business-review`, { decision: "APPROVE", note }), "Verified by the rental business");
          }}>Approve licence evidence</button>
        )}
        {can("renter_drivers.manage") && (
          <button className="px-2 py-1 rounded border text-xs" onClick={() => {
            const reason = window.prompt("Reason for removing this driver");
            if (reason) act(() => api.post(`${base}/remove`, { reason }), "Driver removed (history kept)");
          }}>Remove</button>
        )}
      </div>
    </div>
  );
}

function AddDriver({ role, bookingId, renterName, onClose, onDone }) {
  const [renterDriving, setRenterDriving] = useState(role === "PRIMARY");
  const [details, setDetails] = useState(emptyDetails(role === "PRIMARY" ? renterName : ""));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/renter-drivers/bookings/${bookingId}/drivers`, {
        role, renter_is_driver: role === "PRIMARY" && renterDriving, profile: toPayload(details) });
      toast.success("Driver added. Their details are checked for this vehicle and these dates.");
      onDone();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-slate-300 p-3 space-y-3" role="dialog" aria-label="Add driver" data-testid="renter-add-dialog">
      <div className="flex items-center justify-between">
        <span className="font-medium text-sm">{role === "PRIMARY" ? "Primary driver" : "Additional driver"}</span>
        <button onClick={onClose} aria-label="Close"><X className="w-4 h-4" /></button>
      </div>
      {role === "PRIMARY" && (
        <fieldset className="text-sm flex flex-wrap gap-3">
          <legend className="sr-only">Who will drive?</legend>
          <label className="flex items-center gap-1"><input type="radio" checked={renterDriving}
                 onChange={() => { setRenterDriving(true); setDetails({ ...details, legal_name: renterName }); }} /> The renter will drive</label>
          <label className="flex items-center gap-1"><input type="radio" checked={!renterDriving}
                 onChange={() => { setRenterDriving(false); setDetails({ ...details, legal_name: "" }); }} /> Someone else will drive</label>
        </fieldset>
      )}
      <DriverDetailsForm value={details} onChange={setDetails} idPrefix="staff-add" />
      <button disabled={busy || !details.legal_name.trim()} onClick={submit} className="px-3 py-1.5 rounded-lg btn-brand text-xs disabled:opacity-50">Add and check eligibility</button>
    </div>
  );
}

function InviteDriver({ bookingId, onClose, onDone }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const submit = async () => {
    try {
      await api.post(`/renter-drivers/bookings/${bookingId}/invitations`, { name, email });
      toast.success("Invitation sent. The driver enters their own details.");
      onDone();
    } catch (err) { toast.error(errorMessage(err)); }
  };
  return (
    <div className="rounded-lg border border-slate-300 p-3 space-y-2" role="dialog" aria-label="Invite driver">
      <div className="flex items-center justify-between"><span className="font-medium text-sm">Invite a driver</span>
        <button onClick={onClose} aria-label="Close"><X className="w-4 h-4" /></button></div>
      <label className="block text-sm"><span className="text-xs text-slate-600">Name</span>
        <input className="mt-1 w-full px-3 py-2 rounded-lg border text-sm" value={name} onChange={(e) => setName(e.target.value)} /></label>
      <label className="block text-sm"><span className="text-xs text-slate-600">Email</span>
        <input type="email" className="mt-1 w-full px-3 py-2 rounded-lg border text-sm" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
      <button disabled={!name || !email} onClick={submit} className="px-3 py-1.5 rounded-lg btn-brand text-xs disabled:opacity-50">Send invitation</button>
    </div>
  );
}
